/**
 * Arena invitation application service — create / accept / decline / share URLs.
 * Reuses match access grants + Room Session Shell entry. No ADR-028 model changes.
 */

import { getCurrentAuthUser } from "../repositories/authRepository";
import * as matchRepository from "../repositories/matchRepository";
import * as matchAccessRepository from "../repositories/matchAccessRepository";
import * as playerRepository from "../repositories/playerRepository";
import * as arenaInvitationRepository from "../repositories/arenaInvitationRepository";
import { authUserDisplayHint } from "../domain/auth/rallyAllyAuthEmail";
import { resolveArenaRosterLabel } from "../domain/identity/playerNameParts";
import {
  ARENA_INVITATION_STATUSES,
  ARENA_INVITATION_STATUS_LABELS,
  arenaInvitationJoinPath,
  buildArenaInvitationDocument,
  isArenaInvitationExpired,
  resolveArenaInvitationDisplayStatus,
} from "../domain/arenaInvitation/arenaInvitationTypes";
import { normalizeMatchRoomCode } from "../utils/matchRoomCode";
import { writeStoredSession } from "../utils/storedSession";
import { recordRoomRecent } from "../utils/roomRecents";
import { clearRoomResumeSuppress } from "./session/roomResumeSuppress";
import { notify } from "./notificationService";
import { NOTIFICATION_KINDS } from "../domain/notifications/notificationTypes";
import { acknowledgeSportSelectionThisSession } from "../domain/sport/sportSelectionFrequency";
import {
  hasChosenActiveSport,
  setActiveSportId,
  getActiveSportId,
} from "../domain/sport/activeSport";
import {
  ensureConnectedUserInRegistry,
  ensureKnownLinkedPlayerOnRoster,
  findAnyRegistryEntryForUser,
} from "../features/registry/services/roomRegistryService";
import { normRegistryEntries, normRoomMembers } from "../features/registry/utils/normalize";
import {
  fetchRegistryState,
  persistRegistryState,
} from "../features/registry/repository/registryRepository";

function currentUid() {
  return getCurrentAuthUser()?.uid || null;
}

function displayNameOfUser() {
  const u = getCurrentAuthUser();
  return authUserDisplayHint(u) || "Player";
}

/**
 * Invitation accept must put the player on the Arena roster (not access-only).
 * Best-effort — session entry still proceeds if roster write fails.
 * Prefer known playerId (no identity writes). Host sync is the durable backstop.
 * @param {{
 *   room: string;
 *   uid: string;
 *   displayName?: string | null;
 *   playerId?: string | null;
 * }} args
 */
async function addAcceptingUserToArenaRoster({ room, uid, displayName, playerId }) {
  if (!room || !uid) return { ok: false, reason: "invalid" };
  try {
    const state = await fetchRegistryState(room);
    const name = displayName || displayNameOfUser() || "Player";
    let result;
    if (playerId) {
      result = ensureKnownLinkedPlayerOnRoster({
        roomId: room,
        entries: state.registryEntries || [],
        members: state.roomMembers || [],
        userId: uid,
        playerId,
        displayName: name,
        addedBy: uid,
        method: "joined",
        // Explicit accept may restore a previously removed roster seat.
        reactivateInactive: true,
      });
    } else {
      // Always roster as player — invite grant role may be spectator.
      result = await ensureConnectedUserInRegistry({
        roomId: room,
        entries: state.registryEntries || [],
        members: state.roomMembers || [],
        userId: uid,
        displayName: name,
        method: "joined",
        sessionRole: "player",
      });
    }
    if (!result.changed) return { ok: true, changed: false };
    let written = await persistRegistryState(room, {
      registryEntries: result.entries,
      roomMembers: result.members,
    });
    // One retry — grant/rules races right after createAccessGrant.
    if (!written) {
      await new Promise((r) => setTimeout(r, 250));
      written = await persistRegistryState(room, {
        registryEntries: result.entries,
        roomMembers: result.members,
      });
    }
    return { ok: Boolean(written), changed: true, written: Boolean(written) };
  } catch (err) {
    console.warn("[arena-invite] roster add failed", err?.message || err);
    return { ok: false, reason: "roster_write_failed" };
  }
}

/**
 * Host backstop: accepted invitees → Arena roster (organizer write permissions).
 * Does not call ensurePlayerForUser for other users — uses invitation playerId.
 *
 * @param {string} arenaId
 * @param {object[]} invitations
 */
export async function syncAcceptedInviteesToArenaRoster(arenaId, invitations) {
  const uid = currentUid();
  const room = normalizeMatchRoomCode(arenaId);
  if (!uid || !room) return { ok: false, reason: "invalid" };

  const match = await matchRepository.fetchMatch(room);
  if (!match || !assertOrganizer(match, uid)) {
    return { ok: false, reason: "not_organizer" };
  }

  const state = await fetchRegistryState(room);
  let entries = normRegistryEntries(state.registryEntries || [], room);
  let members = normRoomMembers(state.roomMembers || [], room);
  let changed = false;

  for (const inv of invitations || []) {
    const status = resolveArenaInvitationDisplayStatus(inv);
    const isLink = inv?.kind === "link";
    // Link invites stamp acceptedByUid; player invites use invitedUserId.
    const inviteeUid = isLink
      ? inv?.acceptedByUid || inv?.invitedUserId || null
      : inv?.invitedUserId || inv?.acceptedByUid || null;
    const playerId = inv?.playerId || inv?.acceptedPlayerId || null;
    let displayName =
      inv?.playerName || inv?.acceptedPlayerName || "Player";

    // Player invites: status accepted. Link invites: acceptedByUid set while pending.
    if (inv?.kind === "referee") continue;
    const isAcceptedPlayer =
      !isLink && status === ARENA_INVITATION_STATUSES.ACCEPTED;
    const isLinkJoiner = isLink && Boolean(inv?.acceptedByUid && playerId);
    if (!isAcceptedPlayer && !isLinkJoiner) continue;
    if (!inviteeUid || !playerId) continue;

    // Respect host remove/kick: never revive an inactive roster row.
    const existing = findAnyRegistryEntryForUser(entries, playerId, inviteeUid);
    if (existing && existing.active === false) continue;

    try {
      const linked = await playerRepository.findById(playerId);
      if (linked) {
        displayName = resolveArenaRosterLabel(linked, displayName);
      } else {
        displayName = resolveArenaRosterLabel(
          { displayName },
          displayName
        );
      }
    } catch {
      displayName = resolveArenaRosterLabel({ displayName }, displayName);
    }

    // Already on roster — still refresh a stuck "Player" placeholder to login id / firstName.
    if (existing && existing.active !== false) {
      if (
        existing.displayName &&
        existing.displayName !== displayName &&
        (String(existing.displayName).toLowerCase() === "player" ||
          displayName.toLowerCase() !== "player")
      ) {
        const refreshed = ensureKnownLinkedPlayerOnRoster({
          roomId: room,
          entries,
          members,
          userId: inviteeUid,
          playerId,
          displayName,
          addedBy: uid,
          method: "joined",
        });
        if (refreshed.changed) {
          entries = refreshed.entries;
          members = refreshed.members;
          changed = true;
        }
      }
      continue;
    }

    const result = ensureKnownLinkedPlayerOnRoster({
      roomId: room,
      entries,
      members,
      userId: inviteeUid,
      playerId,
      displayName,
      addedBy: uid,
      method: "joined",
    });
    if (result.changed) {
      entries = result.entries;
      members = result.members;
      changed = true;
    }
  }

  if (!changed) return { ok: true, changed: false };
  const written = await persistRegistryState(room, {
    registryEntries: entries,
    roomMembers: members,
  });
  return { ok: Boolean(written), changed: true };
}

/**
 * @param {string} arenaId room code
 * @param {{ fromServer?: boolean }} [opts]
 */
async function loadArenaMatch(arenaId, opts = {}) {
  const room = normalizeMatchRoomCode(arenaId);
  if (!room) return { ok: false, reason: "not_found" };
  const match = opts.fromServer
    ? await matchRepository.fetchMatchFromServer(room)
    : await matchRepository.fetchMatch(room);
  if (!match) return { ok: false, reason: "not_found", room };
  if (match.status === "cancelled" || match.archived === true || match.archivedAt) {
    return { ok: false, reason: "archived", room, match };
  }
  return { ok: true, room, match };
}

function assertOrganizer(match, uid) {
  if (!uid) return false;
  return (
    match.createdBy === uid ||
    match.deputyRefereeUid === uid ||
    match.ownerUid === uid
  );
}

/**
 * Invite a registered player into an Arena.
 * @param {{
 *   arenaId: string;
 *   playerId: string;
 *   arenaName?: string;
 * }} args
 */
export async function invitePlayerToArena({ arenaId, playerId, arenaName }) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const loaded = await loadArenaMatch(arenaId);
  if (!loaded.ok) {
    const err = new Error(
      loaded.reason === "archived" ? "Arena is archived." : "Arena not found."
    );
    err.code = loaded.reason;
    throw err;
  }
  if (!assertOrganizer(loaded.match, uid)) {
    const err = new Error("Only the Arena organizer can invite players.");
    err.code = "forbidden";
    throw err;
  }

  const player = await playerRepository.findById(playerId);
  if (!player || player.isActive === false) {
    const err = new Error("Player not found.");
    err.code = "player_not_found";
    throw err;
  }
  if (!player.userId) {
    const err = new Error(
      "That player is not linked to a registered account yet."
    );
    err.code = "player_unlinked";
    throw err;
  }

  const existing = (
    await arenaInvitationRepository.listArenaInvitationsForArena(loaded.room, {
      max: 80,
    })
  ).find(
    (inv) =>
      inv.playerId === player.playerId &&
      inv.status === ARENA_INVITATION_STATUSES.PENDING &&
      !isArenaInvitationExpired(inv)
  );
  if (existing) {
    return { invitation: existing, reused: true };
  }

  const invitation = buildArenaInvitationDocument({
    arenaId: loaded.room,
    arenaName:
      arenaName ||
      loaded.match.displayName ||
      loaded.match.name ||
      loaded.room,
    playerId: player.playerId,
    playerName: player.displayName || "Player",
    invitedUserId: player.userId,
    invitedBy: uid,
    invitedByName: displayNameOfUser(),
    kind: "player",
  });

  await arenaInvitationRepository.createArenaInvitation(invitation);

  // Durable inbox for invitee (sync bridge remains as backup / backfill).
  if (player.userId) {
    void publishArenaInvitationNotification(invitation, player.userId);
  }

  return { invitation, reused: false };
}

/**
 * Invite an account as a match referee. They are not added to the player roster.
 * @param {{ arenaId: string, playerId: string, arenaName?: string }} args
 */
export async function inviteRefereeToArena({ arenaId, playerId, arenaName }) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const loaded = await loadArenaMatch(arenaId);
  if (!loaded.ok) {
    const err = new Error(
      loaded.reason === "archived" ? "Arena is archived." : "Arena not found."
    );
    err.code = loaded.reason;
    throw err;
  }
  if (!assertOrganizer(loaded.match, uid)) {
    const err = new Error("Only the organizer can invite a referee.");
    err.code = "forbidden";
    throw err;
  }

  const player = await playerRepository.findById(playerId);
  if (!player || player.isActive === false || !player.userId) {
    const err = new Error("Choose a person who already has a RallyAlly account.");
    err.code = "player_unlinked";
    throw err;
  }
  if (player.userId === uid) {
    const err = new Error("You are already the organizer.");
    err.code = "self";
    throw err;
  }

  const existing = (
    await arenaInvitationRepository.listArenaInvitationsForArena(loaded.room, { max: 80 })
  ).find(
    (inv) =>
      inv.kind === "referee" &&
      inv.invitedUserId === player.userId &&
      (inv.status === ARENA_INVITATION_STATUSES.PENDING ||
        inv.status === ARENA_INVITATION_STATUSES.ACCEPTED) &&
      !isArenaInvitationExpired(inv)
  );
  if (existing) return { invitation: existing, reused: true };

  const invitation = buildArenaInvitationDocument({
    arenaId: loaded.room,
    arenaName: arenaName || loaded.match.displayName || loaded.match.name || loaded.room,
    playerId: player.playerId,
    playerName: player.displayName || "Referee",
    invitedUserId: player.userId,
    invitedBy: uid,
    invitedByName: displayNameOfUser(),
    kind: "referee",
  });
  await arenaInvitationRepository.createArenaInvitation(invitation);
  await rememberReferee(loaded.match, loaded.room, player.userId);
  void publishArenaInvitationNotification(invitation, player.userId);
  return { invitation, reused: false };
}

async function rememberReferee(match, roomId, userId) {
  const competitionId = match?.activeCompetitionId || match?.arenaId || null;
  if (competitionId) {
    try {
      const { fetchCompetition, updateArena } = await import("../repositories/arenaRepository");
      const competition = await fetchCompetition(competitionId);
      const list = Array.isArray(competition?.refereeUids) ? [...competition.refereeUids] : [];
      if (competition && !list.includes(userId)) {
        list.push(userId);
        await updateArena(competitionId, {
          refereeUids: list.slice(0, 8),
          updatedAtMs: Date.now(),
        });
      }
    } catch (err) {
      console.warn("[arena-invite] referee list on tournament failed", err?.message || err);
    }
  }
  try {
    const { patchMatchFields } = await import("./session/matchDocumentHelpers");
    const list = Array.isArray(match?.refereeUids) ? [...match.refereeUids] : [];
    if (!list.includes(userId)) {
      list.push(userId);
      await patchMatchFields(roomId, {
        refereeUids: list.slice(0, 8),
      });
    }
  } catch (err) {
    console.warn("[arena-invite] referee list on arena failed", err?.message || err);
  }
}

/**
 * Drop a referee from the tournament and the arena match list.
 * Pending invites for that account are cancelled.
 * @param {{ arenaId: string, userId: string }} args
 */
export async function removeTournamentReferee({ arenaId, userId }) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const loaded = await loadArenaMatch(arenaId);
  if (!loaded.ok) throw new Error("Arena not found.");
  if (!assertOrganizer(loaded.match, uid)) {
    throw new Error("Only the organizer can remove a referee.");
  }
  const target = String(userId || "").trim();
  if (!target) return { ok: false };

  const competitionId = loaded.match?.activeCompetitionId || null;
  if (competitionId) {
    const { fetchCompetition, updateArena } = await import("../repositories/arenaRepository");
    const competition = await fetchCompetition(competitionId);
    const list = (competition?.refereeUids || []).filter((id) => id !== target);
    if (competition && list.length !== (competition.refereeUids || []).length) {
      await updateArena(competitionId, { refereeUids: list, updatedAtMs: Date.now() });
    }
  }
  const matchList = (loaded.match?.refereeUids || []).filter((id) => id !== target);
  if (matchList.length !== (loaded.match?.refereeUids || []).length) {
    const { patchMatchFields } = await import("./session/matchDocumentHelpers");
    await patchMatchFields(loaded.room, { refereeUids: matchList });
  }

  const invites = await arenaInvitationRepository.listArenaInvitationsForArena(loaded.room, {
    max: 80,
  });
  for (const inv of invites) {
    if (inv.kind !== "referee" || inv.invitedUserId !== target) continue;
    if (inv.status !== ARENA_INVITATION_STATUSES.PENDING) continue;
    await arenaInvitationRepository.updateArenaInvitation(inv.invitationId, {
      status: ARENA_INVITATION_STATUSES.CANCELLED,
      cancelledAtMs: Date.now(),
    });
  }
  return { ok: true };
}

/**
 * Create or reuse a shareable link/QR invitation (no named player).
 * @param {{ arenaId: string; arenaName?: string; fromServer?: boolean }} args
 */
export async function createArenaLinkInvitation({
  arenaId,
  arenaName,
  fromServer = false,
}) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const loaded = await loadArenaMatch(arenaId, { fromServer });
  if (!loaded.ok) {
    const err = new Error(
      loaded.reason === "archived" ? "Arena is archived." : "Arena not found."
    );
    err.code = loaded.reason;
    throw err;
  }
  if (!assertOrganizer(loaded.match, uid)) {
    const err = new Error("Only the Arena organizer can create invite links.");
    err.code = "forbidden";
    throw err;
  }

  const existing = (
    await arenaInvitationRepository.listArenaInvitationsForArena(loaded.room, {
      max: 40,
    })
  ).find(
    (inv) =>
      inv.kind === "link" &&
      inv.status === ARENA_INVITATION_STATUSES.PENDING &&
      !isArenaInvitationExpired(inv) &&
      inv.invitedBy === uid
  );
  if (existing) {
    return { invitation: existing, reused: true };
  }

  const invitation = buildArenaInvitationDocument({
    arenaId: loaded.room,
    arenaName:
      arenaName ||
      loaded.match.displayName ||
      loaded.match.name ||
      loaded.room,
    invitedBy: uid,
    invitedByName: displayNameOfUser(),
    kind: "link",
    playerId: null,
    invitedUserId: null,
  });
  await arenaInvitationRepository.createArenaInvitation(invitation);
  return { invitation, reused: false };
}

export function publishArenaInvitationNotification(invitation, recipientUserId) {
  if (!invitation?.invitationId) return null;
  const userId = recipientUserId || invitation.invitedUserId || null;
  const referee = invitation.kind === "referee";
  return notify({
    kind: NOTIFICATION_KINDS.ARENA_INVITATION,
    title: referee ? "Referee invitation" : "Arena invitation",
    body: referee
      ? `You're invited to referee ${invitation.arenaName || "a tournament"}`
      : `${invitation.arenaName || "Arena"}${
          invitation.invitedByName ? ` · from ${invitation.invitedByName}` : ""
        }`,
    href: arenaInvitationJoinPath(invitation.invitationId),
    entityType: "arena_invitation",
    entityId: invitation.invitationId,
    invitationId: invitation.invitationId,
    dedupeKey: `arena_invitation:${invitation.invitationId}`,
    userId: userId || undefined,
    meta: {
      invitationId: invitation.invitationId,
      arenaId: invitation.arenaId,
      arenaName: invitation.arenaName,
      organizer: invitation.invitedByName,
    },
  });
}

/**
 * @param {string} invitationId
 */
export async function getArenaInvitation(invitationId) {
  const inv = await arenaInvitationRepository.fetchArenaInvitation(invitationId);
  if (!inv) return { ok: false, reason: "not_found" };
  const status = resolveArenaInvitationDisplayStatus(inv);
  if (status === ARENA_INVITATION_STATUSES.EXPIRED && inv.status === "pending") {
    try {
      await arenaInvitationRepository.updateArenaInvitation(invitationId, {
        status: ARENA_INVITATION_STATUSES.EXPIRED,
      });
    } catch {
      /* best-effort */
    }
  }
  return { ok: true, invitation: { ...inv, status }, displayStatus: status };
}

/**
 * Accept invitation → access grant → stored session path for RoomSessionShell.
 * @param {string} invitationId
 */
export async function acceptArenaInvitation(invitationId) {
  const uid = currentUid();
  if (!uid) {
    return { ok: false, reason: "auth_required" };
  }

  const got = await getArenaInvitation(invitationId);
  if (!got.ok) return got;
  const inv = got.invitation;

  if (got.displayStatus === ARENA_INVITATION_STATUSES.EXPIRED) {
    return { ok: false, reason: "expired", invitation: inv };
  }
  if (got.displayStatus === ARENA_INVITATION_STATUSES.CANCELLED) {
    return { ok: false, reason: "cancelled", invitation: inv };
  }
  if (got.displayStatus === ARENA_INVITATION_STATUSES.DECLINED) {
    return { ok: false, reason: "declined", invitation: inv };
  }

  if (
    (inv.kind === "player" || inv.kind === "referee") &&
    inv.invitedUserId &&
    inv.invitedUserId !== uid
  ) {
    return { ok: false, reason: "wrong_user", invitation: inv };
  }

  const loaded = await loadArenaMatch(inv.arenaId);
  if (!loaded.ok) {
    return {
      ok: false,
      reason: loaded.reason === "archived" ? "archived" : "not_found",
      invitation: inv,
    };
  }

  const existingGrant = await matchAccessRepository.fetchAccessGrant(
    loaded.room,
    uid
  );
  const alreadyJoined = Boolean(existingGrant);

  if (!existingGrant) {
    try {
      await matchAccessRepository.createAccessGrant(loaded.room, uid, {
        role: "spectator",
        source: "arena_invitation",
        invitationId: inv.invitationId,
      });
    } catch (e) {
      if (e?.code === "permission-denied" || e?.code === "PERMISSION_DENIED") {
        return { ok: false, reason: "denied", invitation: inv };
      }
      throw e;
    }
  }

  if (
    (inv.kind === "player" || inv.kind === "referee") &&
    inv.status === ARENA_INVITATION_STATUSES.PENDING
  ) {
    await arenaInvitationRepository.updateArenaInvitation(invitationId, {
      status: ARENA_INVITATION_STATUSES.ACCEPTED,
      acceptedAtMs: Date.now(),
      acceptedByUid: uid,
    });
  } else if (inv.kind === "link") {
    // Keep status pending (multi-use QR). Always stamp *this* joiner — never reuse a
    // previous acceptor's playerId (that steals / blanks roster identity).
    try {
      const { ensurePlayerForUser } = await import("./identityService");
      const authUser = getCurrentAuthUser();
      const player = await ensurePlayerForUser(uid, displayNameOfUser());
      const acceptedPlayerId = player?.playerId || null;
      const acceptedPlayerName = resolveArenaRosterLabel(player, displayNameOfUser(), {
        accountEmail: authUser?.email || null,
        authUser,
      });
      await arenaInvitationRepository.updateArenaInvitation(invitationId, {
        acceptedAtMs: Date.now(),
        acceptedByUid: uid,
        acceptedPlayerId,
        acceptedPlayerName,
      });
      inv.acceptedByUid = uid;
      inv.acceptedPlayerId = acceptedPlayerId;
      inv.acceptedPlayerName = acceptedPlayerName;
    } catch (err) {
      console.warn("[arena-invite] link joiner stamp failed", err?.message || err);
    }
  } else if (
    inv.kind === "player" &&
    inv.status === ARENA_INVITATION_STATUSES.ACCEPTED
  ) {
    /* already accepted — still enter arena */
  }

  const role =
    inv.kind === "referee" ||
    loaded.match.createdBy === uid ||
    loaded.match.deputyRefereeUid === uid ||
    (Array.isArray(loaded.match.refereeUids) && loaded.match.refereeUids.includes(uid))
      ? "referee"
      : existingGrant?.role || "spectator";

  const roomDisplayName =
    inv.arenaName ||
    loaded.match.displayName ||
    loaded.match.name ||
    loaded.room;

  writeStoredSession({
    room: loaded.room,
    role,
    roomDisplayName,
    view: "match",
  });
  clearRoomResumeSuppress();
  recordRoomRecent(uid, {
    room: loaded.room,
    roomDisplayName,
    role,
  });

  const isRefereeInvite = inv.kind === "referee";
  if (!isRefereeInvite) {
  // Joined ≠ access-only: put the invitee on the host's Players roster.
  // Always resolve from *this* user's player profile (link invites have no inv.playerId).
  const authUser = getCurrentAuthUser();
  let resolvedRosterName =
    inv.playerName || inv.acceptedPlayerName || displayNameOfUser();
  let resolvedPlayerId = null;
  try {
    const { ensurePlayerForUser } = await import("./identityService");
    const selfPlayer = await ensurePlayerForUser(uid, resolvedRosterName);
    resolvedPlayerId = selfPlayer?.playerId || null;
    if (selfPlayer) {
      resolvedRosterName = resolveArenaRosterLabel(selfPlayer, resolvedRosterName, {
        accountEmail: authUser?.email || null,
        authUser,
      });
    }
  } catch {
    /* roster add may still try identity path */
  }
  if (!resolvedPlayerId && inv.playerId) {
    try {
      const linked = await playerRepository.findById(inv.playerId);
      if (linked) {
        resolvedPlayerId = linked.playerId;
        resolvedRosterName = resolveArenaRosterLabel(linked, resolvedRosterName, {
          accountEmail: authUser?.email || null,
          authUser,
        });
      }
    } catch {
      /* keep fallback */
    }
  }
  if (!resolvedPlayerId) {
    resolvedPlayerId = inv.acceptedPlayerId || inv.playerId || null;
  }
  resolvedRosterName = resolveArenaRosterLabel(
    { displayName: resolvedRosterName },
    resolvedRosterName,
    { accountEmail: authUser?.email || null, authUser }
  );
  await addAcceptingUserToArenaRoster({
    room: loaded.room,
    uid,
    displayName: resolvedRosterName,
    playerId: resolvedPlayerId,
  });
  // Shell entry also ensures roster; extra retry here for invite races right after grant.
  try {
    const { ensureSessionJoinerOnRoster } = await import(
      "./session/ensureSessionJoinerOnRoster"
    );
    await ensureSessionJoinerOnRoster({
      roomCode: loaded.room,
      uid,
      displayName: resolvedRosterName,
      playerId: resolvedPlayerId,
      role: "player",
      retries: 3,
    });
  } catch (err) {
    console.warn("[arena-invite] session roster ensure failed", err?.message || err);
  }
  }

  // Do not block Arena entry with Global Sport Mode overlay.
  acknowledgeSportSelectionThisSession();
  if (!hasChosenActiveSport()) {
    setActiveSportId(getActiveSportId());
  }

  const sessionPath =
    inv.kind === "referee"
      ? `/room/${encodeURIComponent(loaded.room)}/session?tab=overview`
      : `/room/${encodeURIComponent(loaded.room)}/session?tab=${
          role === "referee" ? "match" : "players"
        }`;

  return {
    ok: true,
    alreadyJoined,
    invitation: inv,
    room: loaded.room,
    roomDisplayName,
    role,
    sessionPath,
  };
}

/**
 * @param {string} invitationId
 */
export async function declineArenaInvitation(invitationId) {
  const uid = currentUid();
  if (!uid) return { ok: false, reason: "auth_required" };
  const got = await getArenaInvitation(invitationId);
  if (!got.ok) return got;
  const inv = got.invitation;
  if (inv.invitedUserId && inv.invitedUserId !== uid) {
    return { ok: false, reason: "wrong_user", invitation: inv };
  }
  if (got.displayStatus !== ARENA_INVITATION_STATUSES.PENDING) {
    return { ok: false, reason: got.displayStatus, invitation: inv };
  }
  await arenaInvitationRepository.updateArenaInvitation(invitationId, {
    status: ARENA_INVITATION_STATUSES.DECLINED,
    declinedAtMs: Date.now(),
  });
  return { ok: true, invitation: { ...inv, status: "declined" } };
}

/**
 * @param {string} invitationId
 */
export async function cancelArenaInvitation(invitationId) {
  const uid = currentUid();
  if (!uid) return { ok: false, reason: "auth_required" };
  const got = await getArenaInvitation(invitationId);
  if (!got.ok) return got;
  const inv = got.invitation;
  if (inv.invitedBy !== uid) {
    return { ok: false, reason: "forbidden", invitation: inv };
  }
  if (got.displayStatus !== ARENA_INVITATION_STATUSES.PENDING) {
    return { ok: false, reason: got.displayStatus, invitation: inv };
  }
  await arenaInvitationRepository.updateArenaInvitation(invitationId, {
    status: ARENA_INVITATION_STATUSES.CANCELLED,
  });
  return { ok: true };
}

/**
 * After host removes someone from the roster, drop their player invites so
 * host sync cannot put them back from a lingering "accepted" invitation.
 * @param {{
 *   arenaId: string;
 *   playerId?: string | null;
 *   userId?: string | null;
 * }} args
 */
export async function revokeArenaInvitationsForRosterPlayer({
  arenaId,
  playerId,
  userId,
}) {
  const uid = currentUid();
  const room = String(arenaId || "").trim();
  const pid = String(playerId || "").trim();
  const inviteeUid = String(userId || "").trim();
  if (!uid || !room || (!pid && !inviteeUid)) {
    return { ok: false, reason: "invalid" };
  }
  try {
    const loaded = await loadArenaMatch(room);
    if (!loaded.ok || !assertOrganizer(loaded.match, uid)) {
      return { ok: false, reason: "forbidden" };
    }
    const list = await arenaInvitationRepository.listArenaInvitationsForArena(
      loaded.room,
      { max: 80 }
    );
    let n = 0;
    for (const inv of list || []) {
      if (inv?.kind === "link") continue;
      const match =
        (pid && inv.playerId === pid) ||
        (inviteeUid && inv.invitedUserId === inviteeUid);
      if (!match) continue;
      if (
        inv.status === ARENA_INVITATION_STATUSES.CANCELLED ||
        inv.status === ARENA_INVITATION_STATUSES.DECLINED
      ) {
        continue;
      }
      await arenaInvitationRepository.updateArenaInvitation(inv.invitationId, {
        status: ARENA_INVITATION_STATUSES.CANCELLED,
      });
      n += 1;
    }
    return { ok: true, cancelled: n };
  } catch (err) {
    console.warn("[arenaInvite] revoke for roster player failed", err);
    return { ok: false, reason: "error" };
  }
}

export function buildArenaInvitationAbsoluteUrl(invitationId, origin) {
  const path = arenaInvitationJoinPath(invitationId);
  const base = String(origin || "").replace(/\/$/, "");
  return base ? `${base}${path}` : path;
}

/**
 * After arena create: confirm the match is readable from the server, then mint
 * a /join/:invitationId share URL. Do not expose invite UI until this succeeds.
 *
 * @param {{ arenaId: string; arenaName?: string; origin?: string; retries?: number }} args
 * @returns {Promise<{
 *   ok: true;
 *   room: string;
 *   invitation: object;
 *   inviteAbsUrl: string;
 * } | { ok: false; reason: string; message: string }>}
 */
export async function prepareArenaInviteShare({
  arenaId,
  arenaName,
  origin,
  retries = 4,
} = {}) {
  const room = normalizeMatchRoomCode(arenaId);
  if (!room) {
    return {
      ok: false,
      reason: "invalid",
      message: "Invalid arena code.",
    };
  }

  const baseOrigin =
    origin ||
    (typeof window !== "undefined" ? window.location.origin : "");

  let lastReason = "not_found";
  for (let attempt = 0; attempt < Math.max(1, retries); attempt += 1) {
    try {
      const { invitation } = await createArenaLinkInvitation({
        arenaId: room,
        arenaName,
        fromServer: true,
      });
      const inviteAbsUrl = buildArenaInvitationAbsoluteUrl(
        invitation.invitationId,
        baseOrigin
      );
      if (!inviteAbsUrl) {
        return {
          ok: false,
          reason: "invite_url",
          message: "Could not build invite link.",
        };
      }
      return { ok: true, room, invitation, inviteAbsUrl };
    } catch (err) {
      lastReason = err?.code || "create_failed";
      if (attempt < retries - 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 200 * (attempt + 1)));
        continue;
      }
      return {
        ok: false,
        reason: lastReason,
        message:
          lastReason === "not_found"
            ? "Arena is not ready on the server yet. Check your connection and try again."
            : err?.message || "Could not prepare invite link.",
      };
    }
  }

  return {
    ok: false,
    reason: lastReason,
    message: "Could not prepare invite link.",
  };
}

export {
  listArenaInvitationsForArena,
  subscribeArenaInvitations,
  subscribePendingArenaInvitationsForUser,
} from "../repositories/arenaInvitationRepository";

export { arenaInvitationJoinPath, ARENA_INVITATION_STATUS_LABELS };
